import ExtensionErrorCodes from "./extensionUtil/ExtensionErrorCode";
import KioskStrictVerificator from "./extensionUtil/KioskStrictVerificator";
import {TrackJS} from 'trackjs';

export default class UtilService {

    static waitForWindowDimensions = (maxSeconds: number) => {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const intervalId = setInterval(() => {
                // Check if both window.innerWidth and window.innerHeight are defined and are numbers
                if (
                    typeof window !== 'undefined' &&
                    window.innerWidth > 0 &&
                    window.innerHeight > 0
                ) {
                    const secsUntilDimensionsLoaded = (Date.now() - startTime) / 1000;
                    console.log('Window inner dimensions available after: ' + secsUntilDimensionsLoaded + 'secs. '
                        + ', window.innerWidth:' + window.innerWidth
                        + ', window.screen.width:' + window.screen.width
                        + ', window.innerHeight:' + window.innerHeight
                        + ', window.screen.height:' + window.screen.height);
                    clearInterval(intervalId);
                    resolve({
                        width: window.innerWidth,
                        height: window.innerHeight
                    });
                } else if (Date.now() - startTime >= maxSeconds * 1000) {
                    clearInterval(intervalId);
                    reject(new Error(`window.innerWidth and/or window.innerHeight not populated within ${maxSeconds} seconds. `
                        + ', window.innerWidth:' + window.innerWidth
                        + ', window.screen.width:' + window.screen.width
                        + ', window.innerHeight:' + window.innerHeight
                        + ', window.screen.height:' + window.screen.height));
                }
            }, 200); // Check every 100 milliseconds
        });
    }

    static getWhitelistPwaTokenApiUrl = (param: { challengeResponse?: string }) => {
        const marketplaceId = process.env.REACT_APP_MARKETPLACEID;
        const marketPlaceCentralUrl = process.env.REACT_APP_CENTRALISED_MARKETPLACE_URL;
        const verificationUrl = 'ICSXapi/pwa/VerifyPwaResponseToChallenge';
        const setPwaAppToken = 'ICSXapi/pwa/SetPwaAppToken';
        const { challengeResponse } = param;
        
        if (challengeResponse) {
            return marketplaceId ?
                `${marketPlaceCentralUrl}/${verificationUrl}?marketplaceId=${marketplaceId}` :
                `${marketPlaceCentralUrl}/${verificationUrl}`;
        } else {
            return marketplaceId ?
                `${marketPlaceCentralUrl}/${setPwaAppToken}?marketplaceId=${marketplaceId}` :
                `${marketPlaceCentralUrl}/${setPwaAppToken}`;
        }
    }

    static generatePwaAppToken = () => window.crypto?.randomUUID();

    static isAllegedlyInsideChromeApp = () => {
        return window.navigator.userAgent?.includes("CrOS");
    }

    static isKioskModePrevious = () => {
        // Add a tolerance of 2 pixels for dimension comparison
        const tolerance = 2;
        const widthDifference = Math.abs(window.innerWidth - window.screen.width);
        const heightDifference = Math.abs(window.innerHeight - window.screen.height);
        
        const isOk = UtilService.isAllegedlyInsideChromeApp() &&
            widthDifference <= tolerance &&
            heightDifference <= tolerance;
            
        if (!isOk) {
            console.error('MVP1: TT declared not inside Kiosk Mode'
                + ', window.innerWidth:' + window.innerWidth
                + ', window.screen.width:' + window.screen.width
                + ', window.innerHeight:' + window.innerHeight
                + ', window.screen.height:' + window.screen.height
            );
        }

        return isOk;
    }

    static diagnoseKioskMode = async (): Promise<any> => {
        let response;
        try {
            /**
             NOTE: this only pertains to the first part of the kiosk mode strict verification 
             (the FE generation of the challenge) and
              that still requires that the BE "completes" the verification via
              server-to-server communication with 
              the Google verification API which will be initiated by the pwa iframe.
             */
            const producedChallengeInfo = await KioskStrictVerificator.produceChallengeOrGiveUp();
            const responseResult = producedChallengeInfo as any;
            const shouldAllowUserToContinue = responseResult?.error && ExtensionErrorCodes.isRecoverableError(responseResult?.error)
                ? UtilService.isKioskModePrevious() :
                !!responseResult?.challengeResponse;
            const challengeResponse = responseResult?.challengeResponse ? responseResult?.challengeResponse : undefined;
            if (shouldAllowUserToContinue) {
                response = {
                    success: shouldAllowUserToContinue,
                    pwaAppToken: responseResult?.pwaAppToken,
                    challengeResponse,
                    error: responseResult?.error, // needed to indicate to the BE whether or not this is a fallback case
                }
            } else {
                console.warn("User declared as not inside Kiosk Mode", responseResult?.pwaAppToken)
                response = { success: false, error: ['NON_KIOSK_MODE'] }
            }
        } catch (err) {
            console.error('Kiosk verification api failed due to: ', err);
            const pwaAppToken = UtilService.isKioskModePrevious() ? UtilService.generatePwaAppToken() : undefined
            console.log('New fallback pwaAppToken: ', pwaAppToken);
            if (pwaAppToken) {
                TrackJS.addMetadata("pwaAppToken", pwaAppToken);
            }
            response = { success: !!pwaAppToken, pwaAppToken };
        }
        return response;
    }

    static getInitialLandingPageUrl = () => process.env.REACT_APP_MARKETPLACEID ?
        `${process.env.REACT_APP_CENTRALISED_MARKETPLACE_URL}/?marketplaceId=${process.env.REACT_APP_MARKETPLACEID}` :
        `${process.env.REACT_APP_CENTRALISED_MARKETPLACE_URL}/`;

    static generateAppendedPwaTokenParameters = (param: {pwaAppToken: string, challengeResponse?: string, errors?: string[], marketplaceId?: string}) => {
        const {pwaAppToken, challengeResponse, errors, marketplaceId} = param;
        const urlWithoutChallenge = errors?.length ?
        ExtensionErrorCodes.appendParamForStrictFallback(errors, marketplaceId)
        : '';

        if (challengeResponse) {
            const challengeCode = encodeURIComponent(challengeResponse)
            return `&pwaAppToken=${pwaAppToken}&challengeResponse=${challengeCode}`
        }
        return `${urlWithoutChallenge}&pwaAppToken=${pwaAppToken}`
    }

    static whitelistPwaTokenApiUrl = (param: {pwaAppToken: string, challengeResponse?: string, errors?: string[]}) => {
        const {pwaAppToken, challengeResponse, errors} = param;
        const tokenUrl = UtilService.getWhitelistPwaTokenApiUrl({ challengeResponse });
        const marketplaceId = process.env.REACT_APP_MARKETPLACEID;
        return process.env.REACT_APP_MARKETPLACEID ? `${tokenUrl}${UtilService.generateAppendedPwaTokenParameters({pwaAppToken, challengeResponse, errors, marketplaceId})}` :
            `${tokenUrl}?${UtilService.generateAppendedPwaTokenParameters({pwaAppToken, challengeResponse, errors})}`
            ;
    }

    static getUrlLandingPageWithToken = (pwaAppToken: string) => {
        const initialLandingPageUrl = UtilService.getInitialLandingPageUrl();

        return process.env.REACT_APP_MARKETPLACEID ?
            `${initialLandingPageUrl}&pwaAppToken=${pwaAppToken}#chromeKioskLandingPage` :
            `${initialLandingPageUrl}?pwaAppToken=${pwaAppToken}#chromeKioskLandingPage`
    }

    /**
     * In order to be able to tell apart different devices in TrackJS we'll assign an identifier that will be kept in
     * local-storage so that, as opposed to the pwaAppToken, the same device resolves to the same identifier across launches.
     */
    static produceArtificialDeviceIdentifierIfNecessary = () => {
        const key = 'pwaApp/artificialDeviceIdentifier';
        const storedValue = window.localStorage?.getItem(key);
        if (!storedValue) {
            const newDeviceId = window.crypto?.randomUUID();
            window.localStorage?.setItem(key, newDeviceId);
            return newDeviceId;
        }
        return storedValue;
    }
}
